import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DownloadCloud, History, RotateCcw, X } from 'lucide-react';
import {
  INBOUND_RECORDING_STATUSES,
  backfillInboundRecordings,
  completeInboundRecording,
  getInboundRecording,
  listInboundRecordings,
  pollInboundRecordings,
  reprocessInboundRecording,
  type InboundRecording as InboundRecordingSchema,
} from '@mkcore/platform/schema';
import type { z as zz } from '@mkcore/shared';
import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation } from '@/lib/mutation';

type Recording = zz.output<typeof InboundRecordingSchema>;
type Status = (typeof INBOUND_RECORDING_STATUSES)[number];
const search = z.object({ status: z.enum(INBOUND_RECORDING_STATUSES).optional(), q: z.string().optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/admin/inbound-recordings')({ validateSearch: search, component: InboundRecordings });

const recordingsQuery = (s: Search) =>
  infiniteQueryOptions({
    queryKey: ['admin', 'inboundRecording', 'list', s],
    queryFn: ({ pageParam }) => call(listInboundRecordings, { ...(s.status && { status: [s.status] }), ...(s.q && { q: s.q }), ...(pageParam && { cursor: pageParam }) }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
const recordingQuery = (id: string) => queryOptions({ queryKey: ['admin', 'inboundRecording', id], queryFn: () => call(getInboundRecording, { id }) });

const statusTone = (s: Status) => (s === 'processed' ? 'success' : s === 'failed' ? 'danger' : s === 'skipped' || s === 'expired' ? 'outline' : s === 'routed' ? 'info' : 'warning');
const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Your Plaud recordings (ADR-0030, CO-1366): what the app read, whether the transcript is there and what the report
 * routine did. Read your recordings now, or once from a date (the backfill); offer a skipped one again.
 */
function InboundRecordings() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(recordingsQuery(s));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<Recording | null>(null);
  const [backfill, setBackfill] = useState(false);
  const poll = useActionMutation(pollInboundRecordings, {
    invalidate: [['admin', 'inboundRecording']],
    onSuccess: (r) => toast.success(t('inboundRecordings.polled', { added: r.added, routed: r.routed })),
  });
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.communication')}
        title={t('inboundRecordings.title')}
        description={t('inboundRecordings.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setBackfill(true)}>
              <History /> {t('inboundRecordings.backfill')}
            </Button>
            <Button loading={poll.isPending} onClick={() => poll.mutate({ input: {} })}>
              <DownloadCloud /> {t('inboundRecordings.poll')}
            </Button>
          </div>
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('inboundRecordings.name')} className="w-56">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Status | undefined })}>
              <option value="">{t('common.all')}</option>
              {INBOUND_RECORDING_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`inboundRecordings.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('inboundRecordings.empty')} description={t('inboundRecordings.emptyHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('inboundRecordings.startedAt')}</Th>
              <Th>{t('inboundRecordings.name')}</Th>
              <Th className="hidden md:table-cell">{t('inboundRecordings.duration')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden lg:table-cell">{t('inboundRecordings.outcome')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td className="tabular whitespace-nowrap">{formatDateTime(r.startedAt)}</Td>
                <Td>
                  <button type="button" className="text-left font-bold hover:underline" onClick={() => setOpen(r)}>
                    {r.name || r.sourceId}
                  </button>
                  {r.speakers.length > 0 && <span className="block text-xs text-fg-3">{r.speakers.join(', ')}</span>}
                </Td>
                <Td className="tabular hidden md:table-cell">{clock(r.durationMs)}</Td>
                <Td>
                  <span className="flex flex-wrap gap-1">
                    <Badge tone={statusTone(r.status)}>{t(`inboundRecordings.status.${r.status}`)}</Badge>
                    {r.backfill && <Badge tone="outline">{t('inboundRecordings.backfilled')}</Badge>}
                  </span>
                </Td>
                <Td className="hidden text-fg-2 lg:table-cell">{r.outcome ?? r.error ?? '–'}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {open && <RecordingDialog recording={open} onClose={() => setOpen(null)} />}
      {backfill && <BackfillDialog onClose={() => setBackfill(false)} />}
    </>
  );
}

/** One recording: its transcript per segment, the summary of Plaud, and offering it again or skipping it. */
function RecordingDialog({ recording, onClose }: { recording: Recording; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQuery(recordingQuery(recording.id));
  const r = q.data ?? recording;
  const reprocess = useActionMutation(reprocessInboundRecording, { invalidate: [['admin', 'inboundRecording']], success: t('inboundRecordings.reprocessed'), onSuccess: onClose });
  const skip = useActionMutation(completeInboundRecording, { invalidate: [['admin', 'inboundRecording']], onSuccess: onClose });
  const canReprocess = r.status !== 'processed' && r.status !== 'pending';
  const canSkip = r.status === 'routed' || r.status === 'failed' || r.status === 'pending';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{recording.name || recording.sourceId}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 text-sm">
          <p className="text-fg-2">{t('inboundRecordings.meta', { at: formatDateTime(recording.startedAt), duration: clock(recording.durationMs), id: recording.sourceId })}</p>
          {!q.data ? (
            <Skeleton className="h-40" />
          ) : q.data.segments.length === 0 ? (
            <p className="text-fg-3">{t('inboundRecordings.noTranscript')}</p>
          ) : (
            <ol aria-label={t('inboundRecordings.transcript')} className="max-h-96 overflow-auto rounded-md bg-sunken p-3 text-xs">
              {q.data.segments.map((x) => (
                <li key={`${x.startMs}-${x.speaker}`} className="mb-1">
                  <span className="tabular text-fg-3">{clock(x.startMs)}</span> <span className="font-bold">{x.speaker}</span>: {x.text}
                </li>
              ))}
            </ol>
          )}
          {q.data?.summary && (
            <details>
              <summary className="cursor-pointer text-fg-2">{t('inboundRecordings.summary')}</summary>
              <pre className="mt-2 whitespace-pre-wrap rounded-md bg-sunken p-3 text-xs">{q.data.summary}</pre>
            </details>
          )}
        </div>
        <DialogFooter>
          {canSkip && (
            <Button variant="ghost" loading={skip.isPending} onClick={() => skip.mutate({ input: { id: r.id, status: 'skipped', outcome: t('inboundRecordings.skippedByHand') } })}>
              <X /> {t('inboundRecordings.skip')}
            </Button>
          )}
          {canReprocess && (
            <Button variant="secondary" loading={reprocess.isPending} onClick={() => reprocess.mutate({ input: { id: r.id } })}>
              <RotateCcw /> {t('inboundRecordings.reprocess')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The backfill from a date: no reports, only the transcript of the parts meetings already refer to (ADR-0030). */
function BackfillDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [since, setSince] = useState('');
  const start = useActionMutation(backfillInboundRecordings, { invalidate: [['admin', 'inboundRecording']], success: t('inboundRecordings.backfillStarted'), onSuccess: onClose });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('inboundRecordings.backfill')}</DialogTitle>
        </DialogHeader>
        <form
          id="backfill"
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate({ input: { since } });
          }}
        >
          <p className="text-sm text-fg-2">{t('inboundRecordings.backfillHint')}</p>
          <Field label={t('inboundRecordings.since')}>{(p) => <Input {...p} type="date" required value={since} onChange={(e) => setSince(e.target.value)} />}</Field>
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="backfill" disabled={!since} loading={start.isPending}>
            <History /> {t('inboundRecordings.startBackfill')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
